/**
 * Language Type Definitions
 *
 * Type definitions for multi-language support feature.
 * Matches the `languages` database table schema.
 *
 * @see /supabase/migrations/00001_baseline_schema.sql
 * @see /supabase/migrations/00002_seed_data.sql
 */

/**
 * Language codes for MVP supported languages
 * Spanish, French, German, Italian, Portuguese, Catalan
 */
export type MVPLanguageCode = 'es' | 'fr' | 'de' | 'it' | 'pt' | 'ca';

/**
 * All supported language codes (including non-MVP languages)
 */
export type LanguageCode = MVPLanguageCode | 'en' | 'ja' | 'zh';

/**
 * Language entity matching the `languages` database table
 */
export interface Language {
  /** ISO 639-1 language code (e.g., 'es', 'fr') */
  code: LanguageCode;

  /** English name of the language (e.g., 'Spanish') */
  name: string;

  /** Native name of the language (e.g., 'Español') */
  native_name: string;

  /** Flag emoji for the language (e.g., '🇪🇸') */
  flag_emoji: string;

  /** Whether the language is currently active/available */
  is_active: boolean;

  /** Whether text-to-speech is available */
  has_tts: boolean;

  /** Whether speech-to-text is available */
  has_stt: boolean;

  /** Whether grammar checking is available */
  has_grammar_check: boolean;

  /** Default AI tutor name for this language */
  default_ai_tutor_name: string;
}

/**
 * Minimal language representation for dropdowns/selectors
 */
export interface LanguageOption {
  code: LanguageCode;
  name: string;
  native_name: string;
  flag_emoji: string;
}

/**
 * MVP Languages as a readonly array
 * These are the 6 languages enabled for the initial release
 */
export const MVP_LANGUAGE_CODES: readonly MVPLanguageCode[] = [
  'es', // Spanish
  'fr', // French
  'de', // German
  'it', // Italian
  'pt', // Portuguese
  'ca', // Catalan
] as const;

/**
 * Default language when no preference is set
 */
export const DEFAULT_LANGUAGE_CODE: MVPLanguageCode = 'es';

/**
 * Fallback languages data for when database is unavailable
 * Only includes MVP languages
 */
export const FALLBACK_LANGUAGES: readonly Language[] = [
  {
    code: 'es',
    name: 'Spanish',
    native_name: 'Español',
    flag_emoji: '🇪🇸',
    is_active: true,
    has_tts: true,
    has_stt: true,
    has_grammar_check: true,
    default_ai_tutor_name: 'Carlos',
  },
  {
    code: 'fr',
    name: 'French',
    native_name: 'Français',
    flag_emoji: '🇫🇷',
    is_active: true,
    has_tts: true,
    has_stt: true,
    has_grammar_check: true,
    default_ai_tutor_name: 'Marie',
  },
  {
    code: 'de',
    name: 'German',
    native_name: 'Deutsch',
    flag_emoji: '🇩🇪',
    is_active: true,
    has_tts: true,
    has_stt: true,
    has_grammar_check: true,
    default_ai_tutor_name: 'Hans',
  },
  {
    code: 'it',
    name: 'Italian',
    native_name: 'Italiano',
    flag_emoji: '🇮🇹',
    is_active: true,
    has_tts: true,
    has_stt: true,
    has_grammar_check: true,
    default_ai_tutor_name: 'Sofia',
  },
  {
    code: 'pt',
    name: 'Portuguese',
    native_name: 'Português',
    flag_emoji: '🇧🇷',
    is_active: true,
    has_tts: true,
    has_stt: true,
    has_grammar_check: true,
    default_ai_tutor_name: 'Ana',
  },
  {
    code: 'ca',
    name: 'Catalan',
    native_name: 'Català',
    flag_emoji: '🏴󠁥󠁳󠁣󠁴󠁿',
    is_active: true,
    has_tts: true,
    has_stt: true,
    has_grammar_check: true,
    default_ai_tutor_name: 'Jordi',
  },
] as const;

/**
 * Type guard to check if a string is a valid MVP language code
 */
export function isMVPLanguageCode(code: string): code is MVPLanguageCode {
  return MVP_LANGUAGE_CODES.includes(code as MVPLanguageCode);
}

/**
 * Type guard to check if a string is any valid language code
 */
export function isLanguageCode(code: string): code is LanguageCode {
  const allCodes: LanguageCode[] = [...MVP_LANGUAGE_CODES, 'en', 'ja', 'zh'];
  return allCodes.includes(code as LanguageCode);
}
